import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightCircle, FileText, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ListFilterBar from "@/components/ListFilterBar";
import DocumentNumberLink from "@/components/DocumentNumberLink";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import {
  isWithinDateRange,
  useClearFilterParams,
  useFilterParam,
} from "@/hooks/useFilterParam";
import { formatDate, formatNumber } from "@/lib/format";

type PoStatus = "pending" | "in_production" | "completed";

interface ProductionOrderRow {
  id: number;
  production_order_number: string;
  customer_name: string;
  production_date: string | null;
  status: PoStatus;
  total_sheets: number;
  total_length: number;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "in_production", label: "In production" },
  { value: "completed", label: "Completed" },
];

function statusBadgeClass(status: string): string {
  switch (status) {
    case "pending":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
    case "in_production":
      return "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30";
    case "completed":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function statusLabel(status: string): string {
  if (status === "in_production") return "In production";
  if (status === "completed") return "Completed";
  return "Pending";
}

function nextStatus(current: PoStatus): PoStatus | null {
  if (current === "pending") return "in_production";
  if (current === "in_production") return "completed";
  return null;
}

function advanceLabel(current: PoStatus): string {
  const n = nextStatus(current);
  if (n === "in_production") return "Start production";
  if (n === "completed") return "Mark completed";
  return "";
}

export default function ProductionOrders() {
  const nav = useNavigate();
  const { can } = useAuth();
  const canEdit = can("production_orders.write");
  const canStatus = can("production_orders.status") || canEdit;
  const canRead = can("production_orders.read");

  const [rows, setRows] = useState<ProductionOrderRow[]>([]);
  const [search, setSearch] = useFilterParam("q");
  const [docNo, setDocNo] = useFilterParam("doc_no");
  const [customerQuery, setCustomerQuery] = useFilterParam("customer");
  const [statusFilter, setStatusFilter] = useFilterParam("status");
  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const [loading, setLoading] = useState(true);
  const clearFilters = useClearFilterParams([
    "q",
    "doc_no",
    "customer",
    "status",
    "date_from",
    "date_to",
  ]);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<ProductionOrderRow | null>(
    null,
  );
  const [statusNote, setStatusNote] = useState("");
  const [advancing, setAdvancing] = useState(false);

  const customerOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) {
      if (r.customer_name?.trim()) names.add(r.customer_name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const hasAnyFilter = Boolean(
    search || docNo || customerQuery || statusFilter || dateFrom || dateTo,
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const docNeedle = docNo.trim().toLowerCase();
    const custNeedle = customerQuery.trim().toLowerCase();
    const statusNeedle = statusFilter.trim().toLowerCase();
    return rows.filter((r) => {
      const number = r.production_order_number.toLowerCase();
      const cust = r.customer_name.toLowerCase();
      const searchMatch =
        !s || number.includes(s) || cust.includes(s);
      const docMatch = !docNeedle || number.includes(docNeedle);
      const custMatch = !custNeedle || cust.includes(custNeedle);
      const statusMatch =
        !statusNeedle ||
        statusNeedle === "all" ||
        r.status === statusNeedle;
      const dateMatch = isWithinDateRange(r.production_date, dateFrom, dateTo);
      return searchMatch && docMatch && custMatch && statusMatch && dateMatch;
    });
  }, [rows, search, docNo, customerQuery, statusFilter, dateFrom, dateTo]);

  const pg = usePagination(filtered);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, docNo, customerQuery, statusFilter, dateFrom, dateTo]);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get<{ production_orders: ProductionOrderRow[] }>(
        "/production-orders",
      );
      setRows(res.production_orders ?? []);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load production orders");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openStatusDialog(r: ProductionOrderRow) {
    setStatusTarget(r);
    setStatusNote("");
    setStatusOpen(true);
  }

  async function confirmAdvance() {
    if (!statusTarget) return;
    const target = nextStatus(statusTarget.status);
    if (!target) return;
    setAdvancing(true);
    try {
      await api.patch(`/production-orders/${statusTarget.id}/status`, {
        status: target,
        note: statusNote.trim() || undefined,
      });
      toast.success(`Status updated to ${statusLabel(target)}`);
      setStatusOpen(false);
      setStatusTarget(null);
      await load();
    } catch (err) {
      toast.error((err as Error).message || "Status update failed");
    } finally {
      setAdvancing(false);
    }
  }

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Production Orders</h1>
          <p className="text-muted-foreground">
            Factory production orders for roofing sheet manufacturing.
          </p>
        </div>
        {canEdit ? (
          <Button
            onClick={() => nav("/production-orders/new")}
            className="bg-violet-500 hover:bg-violet-400 text-white rounded-xl px-5"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Production Order
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <ListFilterBar
          title="Search Production Order"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search orders..."
          docNo={{
            value: docNo,
            onChange: setDocNo,
            placeholder: "PRO No.",
          }}
          customer={{
            value: customerQuery,
            onChange: setCustomerQuery,
            options: customerOptions,
            placeholder: "Customer",
          }}
          status={{
            value: statusFilter || "all",
            onChange: (v) => setStatusFilter(v === "all" ? "" : v),
            options: STATUS_OPTIONS,
          }}
          dateFrom={{ value: dateFrom, onChange: setDateFrom }}
          dateTo={{ value: dateTo, onChange: setDateTo }}
          hasAnyFilter={hasAnyFilter}
          onClear={clearFilters}
        />

        <Card className="shadow-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>PO Number</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Production Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total Sheets</TableHead>
                <TableHead className="text-right">Total Length</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground py-10"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground py-10"
                  >
                    No production orders yet.
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading &&
                pg.pageItems.map((r, i) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-muted-foreground">
                      {pg.from + i}
                    </TableCell>
                    <TableCell className="font-mono">
                      <DocumentNumberLink
                        to={`/production-orders/${r.id}`}
                        canOpen={canRead}
                      >
                        {r.production_order_number}
                      </DocumentNumberLink>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {r.customer_name || "—"}
                    </TableCell>
                    <TableCell>
                      {r.production_date ? formatDate(r.production_date) : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={statusBadgeClass(r.status)}
                      >
                        {statusLabel(r.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(r.total_sheets)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(r.total_length)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() =>
                            nav(`/production-orders/${r.id}?openPdf=1`)
                          }
                          title="Preview PDF"
                        >
                          <FileText className="h-4 w-4 text-primary" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => nav(`/production-orders/${r.id}`)}
                          title="View / Edit"
                        >
                          <Pencil className="h-4 w-4 text-primary" />
                        </Button>
                        {canStatus && nextStatus(r.status) ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openStatusDialog(r)}
                            title={advanceLabel(r.status)}
                          >
                            <ArrowRightCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {!loading && filtered.length > 0 ? (
            <Pagination
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              from={pg.from}
              to={pg.to}
              onPrev={pg.prev}
              onNext={pg.next}
            />
          ) : null}
        </Card>
      </div>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Advance production status</DialogTitle>
          </DialogHeader>
          {statusTarget ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Order{" "}
                <span className="font-mono font-medium text-foreground">
                  {statusTarget.production_order_number}
                </span>{" "}
                will move from{" "}
                <strong>{statusLabel(statusTarget.status)}</strong> to{" "}
                <strong>
                  {statusLabel(nextStatus(statusTarget.status)!)}
                </strong>
                .
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="status-note">Note (optional)</Label>
                <Textarea
                  id="status-note"
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  rows={3}
                  placeholder="Factory note..."
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmAdvance} disabled={advancing}>
              {advancing
                ? "Updating..."
                : statusTarget
                  ? advanceLabel(statusTarget.status)
                  : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
